/**
 * Environment System - Manages time, skybox, and atmospheric effects
 * 
 * Features:
 * - Day/night cycle with smooth transitions
 * - Procedural and cubemap skyboxes
 * - Dynamic lighting based on time
 * - Fog and atmospheric effects
 * - Weather system (extensible)
 * - Physics time scaling
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/environment
 */

import { Scene } from "@babylonjs/core/scene";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { CubeTexture } from "@babylonjs/core/Materials/Textures/cubeTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { IDisposable, IUpdatable } from "../core/Engine.types";
import {
    EnvironmentConfig,
    EnvironmentEventCallback,
    EnvironmentEventType,
    TimeOfDay,
    TimeOfDayConfig,
    TimeState,
    WeatherType,
    TimeAccelerationConfig,
} from "./Environment.types";

// Default time-of-day configurations
const DEFAULT_TIME_CONFIGS: TimeOfDayConfig[] = [
    {
        period: TimeOfDay.DAWN,
        startHour: 5,
        endHour: 7,
        sunDirection: { x: 1, y: -0.2, z: 0 },
        sunColor: { r: 1.0, g: 0.6, b: 0.4 },
        sunIntensity: 0.5,
        ambientColor: { r: 0.4, g: 0.35, b: 0.45 },
        ambientIntensity: 0.3,
        skyColor: { r: 0.6, g: 0.5, b: 0.7, a: 1 },
        horizonColor: { r: 1.0, g: 0.6, b: 0.4 },
        groundColor: { r: 0.2, g: 0.2, b: 0.25 },
        fogColor: { r: 0.5, g: 0.45, b: 0.55 },
        fogDensity: 0.005,
        shadowDarkness: 0.5,
        skySettings: { inclination: 0.05, turbidity: 5 },
    },
    {
        period: TimeOfDay.MORNING,
        startHour: 7,
        endHour: 10,
        sunDirection: { x: 0.7, y: -0.5, z: 0.3 },
        sunColor: { r: 1.0, g: 0.9, b: 0.8 },
        sunIntensity: 0.7,
        ambientColor: { r: 0.5, g: 0.5, b: 0.55 },
        ambientIntensity: 0.4,
        skyColor: { r: 0.5, g: 0.7, b: 0.95, a: 1 },
        horizonColor: { r: 0.8, g: 0.85, b: 0.9 },
        groundColor: { r: 0.25, g: 0.25, b: 0.3 },
        fogColor: { r: 0.55, g: 0.65, b: 0.8 },
        fogDensity: 0.003,
        shadowDarkness: 0.4,
        skySettings: { inclination: 0.2, turbidity: 3 },
    },
    {
        period: TimeOfDay.NOON,
        startHour: 10,
        endHour: 14,
        sunDirection: { x: 0, y: -1, z: 0.2 },
        sunColor: { r: 1.0, g: 0.98, b: 0.95 },
        sunIntensity: 1.0,
        ambientColor: { r: 0.6, g: 0.6, b: 0.65 },
        ambientIntensity: 0.5,
        skyColor: { r: 0.4, g: 0.65, b: 1.0, a: 1 },
        horizonColor: { r: 0.7, g: 0.8, b: 0.95 },
        groundColor: { r: 0.3, g: 0.3, b: 0.35 },
        fogColor: { r: 0.5, g: 0.7, b: 0.9 },
        fogDensity: 0.002,
        shadowDarkness: 0.3,
        skySettings: { inclination: 0.5, turbidity: 2 },
    },
    {
        period: TimeOfDay.AFTERNOON,
        startHour: 14,
        endHour: 17,
        sunDirection: { x: -0.5, y: -0.7, z: 0.3 },
        sunColor: { r: 1.0, g: 0.95, b: 0.85 },
        sunIntensity: 0.85,
        ambientColor: { r: 0.55, g: 0.55, b: 0.5 },
        ambientIntensity: 0.45,
        skyColor: { r: 0.45, g: 0.65, b: 0.95, a: 1 },
        horizonColor: { r: 0.75, g: 0.8, b: 0.85 },
        groundColor: { r: 0.28, g: 0.28, b: 0.25 },
        fogColor: { r: 0.55, g: 0.7, b: 0.85 },
        fogDensity: 0.0025,
        shadowDarkness: 0.35,
        skySettings: { inclination: 0.35, turbidity: 2.5 },
    },
    {
        period: TimeOfDay.DUSK,
        startHour: 17,
        endHour: 19,
        sunDirection: { x: -1, y: -0.2, z: 0 },
        sunColor: { r: 1.0, g: 0.5, b: 0.3 },
        sunIntensity: 0.5,
        ambientColor: { r: 0.45, g: 0.35, b: 0.4 },
        ambientIntensity: 0.3,
        skyColor: { r: 0.7, g: 0.4, b: 0.5, a: 1 },
        horizonColor: { r: 1.0, g: 0.5, b: 0.3 },
        groundColor: { r: 0.2, g: 0.15, b: 0.2 },
        fogColor: { r: 0.6, g: 0.45, b: 0.5 },
        fogDensity: 0.004,
        shadowDarkness: 0.5,
        skySettings: { inclination: 0.05, turbidity: 5 },
    },
    {
        period: TimeOfDay.EVENING,
        startHour: 19,
        endHour: 21,
        sunDirection: { x: -0.5, y: 0.1, z: 0.2 },
        sunColor: { r: 0.3, g: 0.3, b: 0.5 },
        sunIntensity: 0.2,
        ambientColor: { r: 0.2, g: 0.2, b: 0.3 },
        ambientIntensity: 0.2,
        skyColor: { r: 0.15, g: 0.15, b: 0.3, a: 1 },
        horizonColor: { r: 0.3, g: 0.25, b: 0.4 },
        groundColor: { r: 0.1, g: 0.1, b: 0.15 },
        fogColor: { r: 0.15, g: 0.15, b: 0.25 },
        fogDensity: 0.005,
        shadowDarkness: 0.6,
        skySettings: { inclination: -0.1, turbidity: 3 },
    },
    {
        period: TimeOfDay.NIGHT,
        startHour: 21,
        endHour: 24,
        sunDirection: { x: 0, y: 0.5, z: 0.5 },
        sunColor: { r: 0.2, g: 0.2, b: 0.4 },
        sunIntensity: 0.1,
        ambientColor: { r: 0.1, g: 0.1, b: 0.2 },
        ambientIntensity: 0.15,
        skyColor: { r: 0.05, g: 0.05, b: 0.15, a: 1 },
        horizonColor: { r: 0.1, g: 0.1, b: 0.2 },
        groundColor: { r: 0.05, g: 0.05, b: 0.1 },
        fogColor: { r: 0.08, g: 0.08, b: 0.15 },
        fogDensity: 0.006,
        shadowDarkness: 0.7,
        skySettings: { inclination: -0.3, turbidity: 2 },
    },
    {
        period: TimeOfDay.MIDNIGHT,
        startHour: 0,
        endHour: 5,
        sunDirection: { x: 0, y: 1, z: 0 },
        sunColor: { r: 0.15, g: 0.15, b: 0.3 },
        sunIntensity: 0.05,
        ambientColor: { r: 0.08, g: 0.08, b: 0.15 },
        ambientIntensity: 0.1,
        skyColor: { r: 0.02, g: 0.02, b: 0.08, a: 1 },
        horizonColor: { r: 0.05, g: 0.05, b: 0.1 },
        groundColor: { r: 0.03, g: 0.03, b: 0.05 },
        fogColor: { r: 0.05, g: 0.05, b: 0.1 },
        fogDensity: 0.007,
        shadowDarkness: 0.8,
        skySettings: { inclination: -0.5, turbidity: 1.5 },
    },
];

/**
 * Default time acceleration configuration
 */
const DEFAULT_TIME_ACCELERATION: Required<TimeAccelerationConfig> = {
    accelerationRate: 30, // Time scale units per second
    decelerationRate: 60, // Faster deceleration for quick stops
    minTimeScale: 0,
    maxTimeScale: 3600, // 1 real second = 1 game hour at max
    smoothTransitions: true,
};

export class EnvironmentManager implements IUpdatable, IDisposable {
    private _scene: Scene;
    private _config: EnvironmentConfig;
    private _timeConfigs: TimeOfDayConfig[];
    
    // Time tracking
    private _currentTime: number = 12; // Hours (0-24)
    private _currentDay: number = 1;
    private _timeScale: number = 1;
    private _targetTimeScale: number = 1;
    private _isPaused: boolean = false;
    private _currentPeriod: TimeOfDay = TimeOfDay.NOON;
    
    // Physics time scale
    private _physicsTimeScale: number = 1;
    private _targetPhysicsTimeScale: number = 1;
    
    // Time acceleration configuration
    private _timeAccelConfig: Required<TimeAccelerationConfig>;
    
    // Skybox
    private _skybox: Mesh | null = null;
    private _skyboxMaterial: StandardMaterial | null = null;
    
    // Lighting references (managed externally but controlled by this system)
    private _sunLight: DirectionalLight | null = null;
    private _ambientLight: HemisphericLight | null = null;
    
    // Weather
    private _currentWeather: WeatherType = WeatherType.CLEAR;
    
    // Event listeners
    private _eventListeners: Map<EnvironmentEventType, EnvironmentEventCallback[]> = new Map();
    
    // Cached state
    private _cachedTimeState: TimeState | null = null;
    private _lastUpdateTime: number = -1;

    public get currentTime(): number {
        return this._currentTime;
    }

    public get currentDay(): number {
        return this._currentDay;
    }

    public get timeOfDay(): TimeOfDay {
        return this._currentPeriod;
    }

    public get weather(): WeatherType {
        return this._currentWeather;
    }

    public get isPaused(): boolean {
        return this._isPaused;
    }

    public get timeScale(): number {
        return this._timeScale;
    }
    
    public get targetTimeScale(): number {
        return this._targetTimeScale;
    }

    public get physicsTimeScale(): number {
        return this._physicsTimeScale;
    }
    
    public get targetPhysicsTimeScale(): number {
        return this._targetPhysicsTimeScale;
    }
    
    /**
     * Check if time scale is accelerating
     */
    public get isAccelerating(): boolean {
        return this._timeScale < this._targetTimeScale;
    }
    
    /**
     * Check if time scale is decelerating
     */
    public get isDecelerating(): boolean {
        return this._timeScale > this._targetTimeScale;
    }

    public get skybox(): Mesh | null {
        return this._skybox;
    }

    constructor(scene: Scene, config?: EnvironmentConfig) {
        this._scene = scene;
        this._config = {
            initialTime: 12,
            timeScale: 60, // 1 real minute = 1 game hour by default
            enableDayNightCycle: true,
            enableFog: true,
            fogMode: 3,
            weather: WeatherType.CLEAR,
            physicsTimeScale: 1,
            ...config,
        };
        
        this._timeConfigs = config?.timeOfDayConfigs || DEFAULT_TIME_CONFIGS;
        this._currentTime = this._config.initialTime!;
        this._timeScale = this._config.timeScale!;
        this._targetTimeScale = this._timeScale;
        this._currentWeather = this._config.weather!;
        this._physicsTimeScale = this._config.physicsTimeScale!;
        this._targetPhysicsTimeScale = this._physicsTimeScale;
        
        // Initialize time acceleration config
        this._timeAccelConfig = {
            ...DEFAULT_TIME_ACCELERATION,
            ...config?.timeAcceleration,
        };
        
        // Initialize
        this._updateCurrentPeriod();
        this._setupFog();
    }

    /**
     * Set references to scene lights for dynamic control
     */
    public setLights(sunLight: DirectionalLight, ambientLight?: HemisphericLight): void {
        this._sunLight = sunLight;
        this._ambientLight = ambientLight || null;
    }

    /**
     * Create a procedural gradient skybox
     */
    public createProceduralSkybox(size: number = 1000): Mesh {
        this._disposeSkybox();
        
        this._skybox = MeshBuilder.CreateBox("skybox", { size }, this._scene);
        this._skyboxMaterial = new StandardMaterial("skyboxMaterial", this._scene);
        
        this._skyboxMaterial.backFaceCulling = false;
        this._skyboxMaterial.disableLighting = true;
        this._skybox.material = this._skyboxMaterial;
        this._skybox.infiniteDistance = true;
        this._skybox.renderingGroupId = 0;
        
        // Apply current time colors
        this._updateSkyboxColors();
        
        console.log("EnvironmentManager: Procedural skybox created");
        return this._skybox;
    }

    /**
     * Create skybox from cubemap textures
     */
    public createCubemapSkybox(
        rootUrl: string,
        extensions: string[] = ["_px.jpg", "_py.jpg", "_pz.jpg", "_nx.jpg", "_ny.jpg", "_nz.jpg"],
        size: number = 1000
    ): Mesh {
        this._disposeSkybox();
        
        this._skybox = MeshBuilder.CreateBox("skybox", { size }, this._scene);
        this._skyboxMaterial = new StandardMaterial("skyboxMaterial", this._scene);
        
        this._skyboxMaterial.backFaceCulling = false;
        this._skyboxMaterial.disableLighting = true;
        
        const cubeTexture = new CubeTexture(rootUrl, this._scene, extensions);
        this._skyboxMaterial.reflectionTexture = cubeTexture;
        this._skyboxMaterial.reflectionTexture.coordinatesMode = Texture.SKYBOX_MODE;
        
        this._skybox.material = this._skyboxMaterial;
        this._skybox.infiniteDistance = true;
        
        console.log("EnvironmentManager: Cubemap skybox created");
        return this._skybox;
    }

    /**
     * Set time directly (0-24)
     */
    public setTime(hours: number): void {
        const previousPeriod = this._currentPeriod;
        this._currentTime = hours % 24;
        if (this._currentTime < 0) this._currentTime += 24;
        
        this._updateCurrentPeriod();
        this._applyTimeOfDaySettings(true);
        
        if (previousPeriod !== this._currentPeriod) {
            this._emitEvent("periodChange", {
                previousValue: previousPeriod,
                currentValue: this._currentPeriod,
                timeState: this.getTimeState(),
            });
        }
        
        this._emitEvent("timeChange", { timeState: this.getTimeState() });
    }

    /**
     * Add time in hours
     */
    public addTime(hours: number): void {
        this.setTime(this._currentTime + hours);
    }

    /**
     * Set time scale (how fast time passes) with optional smooth transition
     * @param scale 1 = real-time, 60 = 1 minute = 1 hour, etc.
     * @param immediate If true, apply immediately without smooth transition
     */
    public setTimeScale(scale: number, immediate: boolean = false): void {
        const clampedScale = Math.max(
            this._timeAccelConfig.minTimeScale,
            Math.min(this._timeAccelConfig.maxTimeScale, scale)
        );
        
        this._targetTimeScale = clampedScale;
        
        if (immediate || !this._timeAccelConfig.smoothTransitions) {
            this._timeScale = clampedScale;
        }
    }

    /**
     * Set physics time acceleration with optional smooth transition
     * @param scale Physics time scale (1 = normal, 0.5 = slow motion, 2 = fast)
     * @param immediate If true, apply immediately without smooth transition
     */
    public setPhysicsTimeScale(scale: number, immediate: boolean = false): void {
        const clampedScale = Math.max(0, Math.min(10, scale)); // Limit physics to 10x
        
        this._targetPhysicsTimeScale = clampedScale;
        
        if (immediate || !this._timeAccelConfig.smoothTransitions) {
            this._physicsTimeScale = clampedScale;
        }
    }
    
    /**
     * Accelerate time scale by amount
     * @param amount Amount to increase time scale by
     */
    public accelerateTime(amount: number = 10): void {
        this.setTimeScale(this._targetTimeScale + amount);
    }
    
    /**
     * Decelerate time scale by amount
     * @param amount Amount to decrease time scale by
     */
    public decelerateTime(amount: number = 10): void {
        this.setTimeScale(this._targetTimeScale - amount);
    }
    
    /**
     * Double the current time scale (fast forward)
     */
    public fastForward(): void {
        this.setTimeScale(this._targetTimeScale * 2);
    }
    
    /**
     * Halve the current time scale (slow down)
     */
    public slowDown(): void {
        this.setTimeScale(this._targetTimeScale / 2);
    }
    
    /**
     * Set to maximum time scale for rapid time passage
     */
    public setMaxSpeed(): void {
        this.setTimeScale(this._timeAccelConfig.maxTimeScale);
    }
    
    /**
     * Reset to default time scale
     * @param immediate Apply immediately without transition
     */
    public resetTimeScale(immediate: boolean = true): void {
        this.setTimeScale(this._config.timeScale!, immediate);
    }
    
    /**
     * Enter slow motion for physics
     * @param scale Slow motion scale (default: 0.25)
     * @param immediate Apply immediately
     */
    public enterPhysicsSlowMotion(scale: number = 0.25, immediate: boolean = false): void {
        this.setPhysicsTimeScale(scale, immediate);
    }
    
    /**
     * Exit physics slow motion
     * @param immediate Apply immediately
     */
    public exitPhysicsSlowMotion(immediate: boolean = false): void {
        this.setPhysicsTimeScale(1, immediate);
    }
    
    /**
     * Toggle physics slow motion
     */
    public togglePhysicsSlowMotion(): void {
        if (this._targetPhysicsTimeScale < 1) {
            this.exitPhysicsSlowMotion();
        } else {
            this.enterPhysicsSlowMotion();
        }
    }
    
    /**
     * Configure time acceleration parameters
     */
    public configureTimeAcceleration(config: Partial<TimeAccelerationConfig>): void {
        this._timeAccelConfig = {
            ...this._timeAccelConfig,
            ...config,
        };
    }
    
    /**
     * Get current time acceleration configuration
     */
    public getTimeAccelerationConfig(): TimeAccelerationConfig {
        return { ...this._timeAccelConfig };
    }

    /**
     * Pause time progression
     */
    public pause(): void {
        this._isPaused = true;
    }

    /**
     * Resume time progression
     */
    public resume(): void {
        this._isPaused = false;
    }

    /**
     * Set weather type
     */
    public setWeather(weather: WeatherType): void {
        const previousWeather = this._currentWeather;
        this._currentWeather = weather;
        
        this._applyWeatherEffects();
        
        this._emitEvent("weatherChange", {
            previousValue: previousWeather,
            currentValue: weather,
        });
    }

    /**
     * Get current time state
     */
    public getTimeState(): TimeState {
        if (this._cachedTimeState && this._lastUpdateTime === this._currentTime) {
            return this._cachedTimeState;
        }
        
        const config = this._getCurrentTimeConfig();
        const duration = this._getPeriodDuration(config);
        const elapsed = this._getElapsedInPeriod(config);
        
        this._cachedTimeState = {
            currentTime: this._currentTime,
            timeOfDay: this._currentPeriod,
            day: this._currentDay,
            periodProgress: elapsed / duration,
            isDaytime: this._isDaytime(),
            sunAngle: this._calculateSunAngle(),
        };
        
        this._lastUpdateTime = this._currentTime;
        return this._cachedTimeState;
    }

    /**
     * Get formatted time string (HH:MM)
     */
    public getFormattedTime(): string {
        const hours = Math.floor(this._currentTime);
        const minutes = Math.floor((this._currentTime % 1) * 60);
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    /**
     * Register event listener
     */
    public addEventListener(event: EnvironmentEventType, callback: EnvironmentEventCallback): void {
        if (!this._eventListeners.has(event)) {
            this._eventListeners.set(event, []);
        }
        this._eventListeners.get(event)!.push(callback);
    }

    /**
     * Remove event listener
     */
    public removeEventListener(event: EnvironmentEventType, callback: EnvironmentEventCallback): void {
        const listeners = this._eventListeners.get(event);
        if (listeners) {
            const index = listeners.indexOf(callback);
            if (index !== -1) {
                listeners.splice(index, 1);
            }
        }
    }

    /**
     * Update environment (call every frame)
     */
    public update(deltaTime: number): void {
        // Update time scale with smooth transition
        this._updateTimeScales(deltaTime);
        
        if (this._isPaused || !this._config.enableDayNightCycle) {
            return;
        }
        
        const previousPeriod = this._currentPeriod;
        const previousDay = this._currentDay;
        
        // Advance time based on time scale
        // timeScale of 60 means 1 real second = 1 game minute
        const hoursToAdd = (deltaTime * this._timeScale) / 3600;
        this._currentTime += hoursToAdd;
        
        // Handle day rollover
        if (this._currentTime >= 24) {
            this._currentTime -= 24;
            this._currentDay++;
            this._emitEvent("dayChange", {
                previousValue: previousDay,
                currentValue: this._currentDay,
            });
        }
        
        // Update period
        this._updateCurrentPeriod();
        
        // Emit period change event
        if (previousPeriod !== this._currentPeriod) {
            this._emitEvent("periodChange", {
                previousValue: previousPeriod,
                currentValue: this._currentPeriod,
                timeState: this.getTimeState(),
            });
            console.log(`EnvironmentManager: Time of day changed to ${this._currentPeriod}`);
        }
        
        // Apply smooth transitions
        this._applyTimeOfDaySettings(false);
    }
    
    /**
     * Update time scales with smooth interpolation
     */
    private _updateTimeScales(deltaTime: number): void {
        // Update environment time scale
        if (Math.abs(this._timeScale - this._targetTimeScale) > 0.01) {
            const rate = this._timeScale < this._targetTimeScale
                ? this._timeAccelConfig.accelerationRate
                : this._timeAccelConfig.decelerationRate;
            
            const change = rate * deltaTime;
            
            if (this._timeScale < this._targetTimeScale) {
                this._timeScale = Math.min(this._targetTimeScale, this._timeScale + change);
            } else {
                this._timeScale = Math.max(this._targetTimeScale, this._timeScale - change);
            }
        } else {
            this._timeScale = this._targetTimeScale;
        }
        
        // Update physics time scale (faster transition)
        const physicsRate = this._timeAccelConfig.decelerationRate * 2;
        if (Math.abs(this._physicsTimeScale - this._targetPhysicsTimeScale) > 0.001) {
            const change = physicsRate * deltaTime;
            
            if (this._physicsTimeScale < this._targetPhysicsTimeScale) {
                this._physicsTimeScale = Math.min(this._targetPhysicsTimeScale, this._physicsTimeScale + change);
            } else {
                this._physicsTimeScale = Math.max(this._targetPhysicsTimeScale, this._physicsTimeScale - change);
            }
        } else {
            this._physicsTimeScale = this._targetPhysicsTimeScale;
        }
    }

    /**
     * Dispose all resources
     */
    public dispose(): void {
        this._disposeSkybox();
        this._eventListeners.clear();
    }

    // ==================== Private Methods ====================

    private _disposeSkybox(): void {
        if (this._skyboxMaterial) {
            this._skyboxMaterial.dispose();
            this._skyboxMaterial = null;
        }
        if (this._skybox) {
            this._skybox.dispose();
            this._skybox = null;
        }
    }

    private _setupFog(): void {
        if (!this._config.enableFog) return;
        
        this._scene.fogMode = this._config.fogMode || 3;
        const config = this._getCurrentTimeConfig();
        this._scene.fogColor = new Color3(
            config.fogColor.r,
            config.fogColor.g,
            config.fogColor.b
        );
        this._scene.fogDensity = config.fogDensity;
    }

    private _updateCurrentPeriod(): void {
        for (const config of this._timeConfigs) {
            if (this._isTimeInPeriod(this._currentTime, config)) {
                this._currentPeriod = config.period;
                return;
            }
        }
    }

    private _isTimeInPeriod(time: number, config: TimeOfDayConfig): boolean {
        if (config.startHour < config.endHour) {
            return time >= config.startHour && time < config.endHour;
        } else {
            // Handle midnight crossing (e.g., 21-5)
            return time >= config.startHour || time < config.endHour;
        }
    }

    private _getCurrentTimeConfig(): TimeOfDayConfig {
        return this._timeConfigs.find(c => c.period === this._currentPeriod) || this._timeConfigs[2];
    }

    private _getNextTimeConfig(): TimeOfDayConfig {
        const currentIndex = this._timeConfigs.findIndex(c => c.period === this._currentPeriod);
        const nextIndex = (currentIndex + 1) % this._timeConfigs.length;
        return this._timeConfigs[nextIndex];
    }

    private _getPeriodDuration(config: TimeOfDayConfig): number {
        if (config.startHour < config.endHour) {
            return config.endHour - config.startHour;
        }
        return (24 - config.startHour) + config.endHour;
    }

    private _getElapsedInPeriod(config: TimeOfDayConfig): number {
        if (config.startHour < config.endHour) {
            return this._currentTime - config.startHour;
        }
        if (this._currentTime >= config.startHour) {
            return this._currentTime - config.startHour;
        }
        return (24 - config.startHour) + this._currentTime;
    }

    private _applyTimeOfDaySettings(immediate: boolean): void {
        const currentConfig = this._getCurrentTimeConfig();
        const nextConfig = this._getNextTimeConfig();
        const duration = this._getPeriodDuration(currentConfig);
        const elapsed = this._getElapsedInPeriod(currentConfig);
        const t = Math.min(1, elapsed / duration);
        
        // Interpolate between current and next period near transitions
        const transitionZone = 0.8; // Start blending in last 20% of period
        const blendT = t > transitionZone ? (t - transitionZone) / (1 - transitionZone) : 0;
        
        // Update scene clear color (sky)
        const skyColor = immediate || blendT === 0 
            ? currentConfig.skyColor 
            : this._lerpColor4(currentConfig.skyColor, nextConfig.skyColor, blendT);
        this._scene.clearColor = new Color4(skyColor.r, skyColor.g, skyColor.b, skyColor.a);
        
        // Update fog
        if (this._config.enableFog) {
            const fogColor = immediate || blendT === 0
                ? currentConfig.fogColor
                : this._lerpColor3(currentConfig.fogColor, nextConfig.fogColor, blendT);
            const fogDensity = immediate || blendT === 0
                ? currentConfig.fogDensity
                : this._lerp(currentConfig.fogDensity, nextConfig.fogDensity, blendT);
            
            this._scene.fogColor = new Color3(fogColor.r, fogColor.g, fogColor.b);
            this._scene.fogDensity = fogDensity;
        }
        
        // Update sun light
        if (this._sunLight) {
            const sunDir = immediate || blendT === 0
                ? currentConfig.sunDirection
                : this._lerpVector3(currentConfig.sunDirection, nextConfig.sunDirection, blendT);
            const sunColor = immediate || blendT === 0
                ? currentConfig.sunColor
                : this._lerpColor3(currentConfig.sunColor, nextConfig.sunColor, blendT);
            const sunIntensity = immediate || blendT === 0
                ? currentConfig.sunIntensity
                : this._lerp(currentConfig.sunIntensity, nextConfig.sunIntensity, blendT);
            
            this._sunLight.direction = new Vector3(sunDir.x, sunDir.y, sunDir.z);
            this._sunLight.diffuse = new Color3(sunColor.r, sunColor.g, sunColor.b);
            this._sunLight.intensity = sunIntensity;
        }
        
        // Update ambient light
        if (this._ambientLight) {
            const ambientColor = immediate || blendT === 0
                ? currentConfig.ambientColor
                : this._lerpColor3(currentConfig.ambientColor, nextConfig.ambientColor, blendT);
            const groundColor = immediate || blendT === 0
                ? currentConfig.groundColor
                : this._lerpColor3(currentConfig.groundColor, nextConfig.groundColor, blendT);
            const ambientIntensity = immediate || blendT === 0
                ? currentConfig.ambientIntensity
                : this._lerp(currentConfig.ambientIntensity, nextConfig.ambientIntensity, blendT);
            
            this._ambientLight.diffuse = new Color3(ambientColor.r, ambientColor.g, ambientColor.b);
            this._ambientLight.groundColor = new Color3(groundColor.r, groundColor.g, groundColor.b);
            this._ambientLight.intensity = ambientIntensity;
        }
        
        // Update skybox if using procedural
        this._updateSkyboxColors();
    }

    private _updateSkyboxColors(): void {
        if (!this._skyboxMaterial) return;
        
        const config = this._getCurrentTimeConfig();
        
        // For a procedural skybox, we use emissive color
        this._skyboxMaterial.emissiveColor = new Color3(
            config.skyColor.r * 0.5,
            config.skyColor.g * 0.5,
            config.skyColor.b * 0.5
        );
    }

    private _applyWeatherEffects(): void {
        // Modify fog and lighting based on weather
        switch (this._currentWeather) {
            case WeatherType.CLOUDY:
                this._scene.fogDensity *= 1.2;
                if (this._sunLight) this._sunLight.intensity *= 0.8;
                break;
            case WeatherType.OVERCAST:
                this._scene.fogDensity *= 1.5;
                if (this._sunLight) this._sunLight.intensity *= 0.5;
                break;
            case WeatherType.FOG:
                this._scene.fogDensity *= 3;
                break;
            case WeatherType.RAIN:
                this._scene.fogDensity *= 2;
                if (this._sunLight) this._sunLight.intensity *= 0.6;
                // TODO: Add rain particle system
                break;
            case WeatherType.STORM:
                this._scene.fogDensity *= 2.5;
                if (this._sunLight) this._sunLight.intensity *= 0.3;
                // TODO: Add lightning and heavy rain
                break;
            default:
                // Clear weather - no modifications
                break;
        }
    }

    private _isDaytime(): boolean {
        return this._currentTime >= 6 && this._currentTime < 19;
    }

    private _calculateSunAngle(): number {
        // Map 6:00 (sunrise) to 0°, 12:00 (noon) to 90°, 18:00 (sunset) to 180°
        if (this._currentTime >= 6 && this._currentTime < 18) {
            return ((this._currentTime - 6) / 12) * 180;
        }
        // Night time - sun below horizon
        return -1;
    }

    private _emitEvent(
        event: EnvironmentEventType,
        data: {
            previousValue?: TimeOfDay | WeatherType | number;
            currentValue?: TimeOfDay | WeatherType | number;
            timeState?: TimeState;
        }
    ): void {
        const listeners = this._eventListeners.get(event);
        if (listeners) {
            for (const callback of listeners) {
                callback(event, data);
            }
        }
    }

    // Interpolation helpers
    private _lerp(a: number, b: number, t: number): number {
        return a + (b - a) * t;
    }

    private _lerpColor3(
        a: { r: number; g: number; b: number },
        b: { r: number; g: number; b: number },
        t: number
    ): { r: number; g: number; b: number } {
        return {
            r: this._lerp(a.r, b.r, t),
            g: this._lerp(a.g, b.g, t),
            b: this._lerp(a.b, b.b, t),
        };
    }

    private _lerpColor4(
        a: { r: number; g: number; b: number; a: number },
        b: { r: number; g: number; b: number; a: number },
        t: number
    ): { r: number; g: number; b: number; a: number } {
        return {
            r: this._lerp(a.r, b.r, t),
            g: this._lerp(a.g, b.g, t),
            b: this._lerp(a.b, b.b, t),
            a: this._lerp(a.a, b.a, t),
        };
    }

    private _lerpVector3(
        a: { x: number; y: number; z: number },
        b: { x: number; y: number; z: number },
        t: number
    ): { x: number; y: number; z: number } {
        return {
            x: this._lerp(a.x, b.x, t),
            y: this._lerp(a.y, b.y, t),
            z: this._lerp(a.z, b.z, t),
        };
    }
}
